import React, { useEffect, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, Btn, Select, Empty } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Logo } from "@/components/Logo";
import { FileText, FileArchive, Files, Eye } from "lucide-react";

const inr = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Payslips() {
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState("");
  const [detail, setDetail] = useState(null); // {run, entries}
  const [clients, setClients] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/payroll/runs").then((r) => setRuns(r.data.filter((x) => ["calculated", "finalized", "locked"].includes(x.status))));
    api.get("/clients").then((r) => setClients(r.data));
  }, []);
  useEffect(() => { if (runId) api.get(`/payroll/runs/${runId}`).then((r) => setDetail(r.data)); else setDetail(null); }, [runId]);
  const cname = (id) => clients.find((c) => c.id === id)?.name || "";
  const run = runs.find((r) => r.id === runId);
  const entries = detail?.entries || [];

  return (
    <div>
      <PageHeader title="Payslips" subtitle="Preview branded payslips, then download PDF / ZIP" />
      <Card className="p-4 mb-4 flex flex-wrap items-center gap-3">
        <Select value={runId} onChange={(e) => setRunId(e.target.value)} className="max-w-xs" data-testid="payslip-run-select">
          <option value="">Select payroll run</option>
          {runs.map((r) => <option key={r.id} value={r.id}>{cname(r.client_id)} — {r.month} ({r.employee_count})</option>)}
        </Select>
        {runId && <>
          <div className="flex-1" />
          <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${runId}/payslips-bundle?mode=combined`, `payslips_${run?.month}.pdf`)} data-testid="combined-pdf-btn"><Files size={16} /> Combined PDF</Btn>
          <Btn onClick={() => downloadFile(`/payroll/runs/${runId}/payslips-bundle?mode=zip`, `payslips_${run?.month}.zip`)} data-testid="zip-btn"><FileArchive size={16} /> Download ZIP</Btn>
        </>}
      </Card>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {entries.length ? (
            <table className="nk-table" data-testid="payslips-table">
              <thead><tr><th>UAN</th><th>Employee</th><th>Designation</th><th>Net Pay</th><th></th></tr></thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="nk-num">{e.snapshot?.uan}</td><td>{e.snapshot?.name}</td><td>{e.snapshot?.designation}</td>
                    <td className="nk-num font-semibold">₹{inr(e.net_pay)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <Btn variant="ghost" onClick={() => setPreview(e)} data-testid={`payslip-preview-${e.snapshot?.uan}`}><Eye size={14} /></Btn>
                        <Btn variant="outline" onClick={() => downloadFile(`/payroll/runs/${runId}/payslips/${e.id}/pdf`, `payslip_${e.snapshot?.uan}.pdf`)} data-testid={`payslip-pdf-${e.snapshot?.uan}`}><FileText size={14} /> PDF</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <Empty title="Select a run" hint="Payslips appear after payroll is calculated." />}
        </div>
      </Card>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Payslip Preview</DialogTitle></DialogHeader>
          {preview && (
            <div className="border border-slate-200 rounded-md p-5" data-testid="payslip-preview">
              <div className="flex items-center justify-between mb-3">
                <Logo onLight height={38} />
                <div className="text-right"><div className="nk-head font-bold">SALARY SLIP</div><div className="text-xs text-slate-500">{run?.month}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-4">
                <div><span className="text-slate-400">Employee:</span> <b>{preview.snapshot?.name}</b></div>
                <div><span className="text-slate-400">UAN:</span> {preview.snapshot?.uan}</div>
                <div><span className="text-slate-400">Designation:</span> {preview.snapshot?.designation}</div>
                <div><span className="text-slate-400">ESI:</span> {preview.snapshot?.esi_number || "—"}</div>
                <div><span className="text-slate-400">Working Days:</span> {preview.working_days}</div>
                <div><span className="text-slate-400">Rate:</span> ₹{preview.wage_rate}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="nk-overline mb-1">Earnings</div>
                  <div className="flex justify-between border-b border-slate-100 py-1"><span>Earned Wage</span><span className="nk-num">₹{inr(preview.earned_wage)}</span></div>
                </div>
                <div>
                  <div className="nk-overline mb-1">Deductions</div>
                  <div className="flex justify-between py-1"><span>Employee PF</span><span className="nk-num">₹{inr(preview.employee_pf)}</span></div>
                  <div className="flex justify-between py-1"><span>Employee ESI</span><span className="nk-num">₹{inr(preview.employee_esi)}</span></div>
                  <div className="flex justify-between py-1"><span>Professional Tax</span><span className="nk-num">₹{inr(preview.pt)}</span></div>
                  <div className="flex justify-between border-t border-slate-200 py-1 font-semibold"><span>Total</span><span className="nk-num">₹{inr(preview.total_deductions)}</span></div>
                </div>
              </div>
              <div className="flex justify-between items-center mt-4 bg-emerald-50 border border-emerald-200 rounded-md px-4 py-2">
                <span className="font-bold text-emerald-800">NET PAY</span>
                <span className="nk-num font-bold text-emerald-800 text-lg">₹{inr(preview.net_pay)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
